<template>
    <flex-element
        :element-type="elementType"
        class="f-relative"
        class-default-inline="f-w-1/1"
        :style="containerStyle"
    >
        <drop-zone-single
            v-for="(element, index) in elementsNew"
            :key="element.id"
            ref="layers"
            v-bind="element"
            class-default="f-h-1/1"
            :style="layerStyle"
            :index="index"
            :pointer="index + 1"
            :display-name="`Layer ${ index + 1 }`"
            @element-updated="elementUpdated"
        />

        <collection-controls
            :visible-index="visibleLayerIndex"
            :default-index="defaultLayerIndex"
            :num-elements="elementsNew.length"
            @add="onAdd"
            @delete="onDelete"
            @previous="onPrevious"
            @next="onNext"
            @star="onSetDefault"
        />
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';

import CollectionControls from '@/components/utilities/collection-controls';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { Organism } from '@/data/data-models';
import { CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';

const LAYER_INDEX_ATTR = 'layer-default-index';
const LAYER_TRANSITION_ATTR = 'layer-transition-speed';

export default {
    name: 'o-layered-container',
    components: { CollectionControls },
    mixins: [baseOrganism, listenOnRootMixin],

    props: {
        displayName: {
            type: String,
            default: 'Layered Container',
        },
    },

    data() {
        return {
            cssDisplay: 'block',
            visibleLayerIndex: -1,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            dynamicMapPreviewMode: state => state.dynamicMapPreviewMode,
        }),

        defaultLayerIndex() {
            if (!this.hasElementData(LAYER_INDEX_ATTR)) return 0;

            return this.getElementData(LAYER_INDEX_ATTR);
        },

        containerStyle() {
            if (this.elementsNew.length) return null;

            // allows the controls to display when no layers exist
            // otherwise the container height will be zero, and the controls will be hidden
            return {
                overflow: 'visible',
            };
        },

        // n.b. this is applied to ALL layers, regardless of whether they are visible or not
        // visibility-based inline styles are set in toggleLayerStyles()
        layerStyle() {
            return {
                transition: `opacity ${ this.speed }s !important`,
            };
        },

        speed() {
            // we don't want to apply transitions while in editing mode
            return !this.previewMode
                ? 0
                : this.getElementData(LAYER_TRANSITION_ATTR) || '0.3'; // should match the default set in layer-transition.vue
        },
    },

    watch: {
        visibleLayerIndex() {
            this.toggleLayerStyles();
            if (!this.dynamicMapPreviewMode) {
                this.clearSelectedElements();
            }
        },
    },

    created() {
        this.listenOnRoot('flex::layered-container::switch', this.onSwitchEvent);

        if (!this.hasElementData(LAYER_INDEX_ATTR)) {
            this.setElementData(LAYER_INDEX_ATTR, 0, { triggerChangeDetection: false });
        }
    },

    async mounted() {
        if (!this.elementsNew.length) {
            this.onAdd(true);
            return;
        }

        await this.$nextTick(); // wait for each layer to render
        await this.$nextTick(); // wait for the contents of each layer to render

        this.visibleLayerIndex = this.getElementData(LAYER_INDEX_ATTR);
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
        }),

        onAdd(isDefaultAdd) {
            const pointer = this.elementsNew.length + 1;
            let element = null;

            element = new Organism({
                id: uuidv4(),
                name: `drop-zone-single_${ pointer }`,
                elementType: 'drop-zone-single',
                pointer,
                classes: '',
                templateType: 'Organism',
                dataSourceId: '00000000-0000-0000-0000-000000000000',
                elements: [],
            });

            this.elementsNew.push(element);
            this.visibleLayerIndex = this.elementsNew.length - 1;

            if (!isDefaultAdd) this.setChangedData(true);
        },

        onDelete(layerIndex) {
            this.elementsNew.splice(layerIndex, 1);
            this.clearSelectedElements();
            this.setChangedData(true);
            this.onNext();
        },

        onPrevious() {
            this.visibleLayerIndex = this.visibleLayerIndex <= 0 ? 0 : this.visibleLayerIndex - 1;
        },

        onNext() {
            const lastIndex = this.elements.length - 1;

            this.visibleLayerIndex = this.visibleLayerIndex >= lastIndex ? lastIndex : this.visibleLayerIndex + 1;
        },

        onSetDefault(layerIndex) {
            this.setElementData(LAYER_INDEX_ATTR, layerIndex);
        },

        onSwitchEvent(eventData) {
            if (this.id === eventData.targetId) {
                this.visibleLayerIndex = eventData.layerNum - 1;
            }
        },

        // we need inline styles with !important (instead of CSS classes) to help ensure styles are not overridden
        // ... and also because we need to manage the transition speed
        toggleLayerStyles() {
            if (!this.$refs.layers) return;

            this.$refs.layers.forEach((layer, layerIndex) => {
                const isVisible = layerIndex === this.visibleLayerIndex;

                if (isVisible) {
                    this.removeHiddenStyles(layer.$el);
                } else {
                    this.addHiddenStyles(layer.$el);

                    // we want to scroll the hidden layers back up to the top, for a better UX in case they are displayed again
                    // the timeout ensures that the scrolling happens when the layer is no longer visible
                    const timeoutDelay = (this.speed * 1000) + 500;

                    window.setTimeout(() => {
                        this.scrollUp(layer.$el);
                    }, timeoutDelay);
                }
            });
        },

        addHiddenStyles(layerElem) {
            layerElem.style.setProperty('position', 'absolute', 'important');
            layerElem.style.setProperty('top', 0, 'important');
            layerElem.style.setProperty('z-index', -1, 'important');
            layerElem.style.setProperty('opacity', 0, 'important');
        },

        removeHiddenStyles(layerElem) {
            layerElem.style.setProperty('z-index', 1, 'important');
            layerElem.style.removeProperty('position');
            layerElem.style.removeProperty('top');
            layerElem.style.removeProperty('opacity');
        },

        scrollUp(layerElem) {
            layerElem.scrollTo(0, 0);
        },
    },
};
</script>
